import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  MessageSquare, Send, ArrowLeft, Loader2, User, Clock, CheckCheck
} from "lucide-react";

export default function MessagesPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [pendingContact, setPendingContact] = useState(null);
  const messagesEndRef = useRef(null);
  const token = localStorage.getItem("token");

  // Handle contact URL parameter
  const contactId = searchParams.get("contact");
  const jobId = searchParams.get("job");

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      toast.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchMessages = useCallback(async (conversationId) => {
    try {
      const response = await axios.get(`${API}/messages/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      toast.error("Failed to load messages");
    }
  }, [token]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Handle new contact from URL
  useEffect(() => {
    if (contactId && !loading && conversations.length >= 0) {
      const existingConv = conversations.find(conv => 
        conv.participants.includes(contactId)
      );
      
      if (existingConv) {
        setSelectedConversation(existingConv);
      } else {
        setPendingContact({ recipientId: contactId, jobId });
      }
    }
  }, [contactId, jobId, loading, conversations]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    // Handle new conversation
    if (pendingContact && !selectedConversation) {
      setSending(true);
      try {
        await axios.post(
          `${API}/messages`,
          {
            recipient_id: pendingContact.recipientId,
            job_id: pendingContact.jobId || null,
            content: newMessage.trim()
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setNewMessage("");
        setPendingContact(null);
        await fetchConversations();
        toast.success("Message sent!");
      } catch (error) {
        toast.error("Failed to send message");
      } finally {
        setSending(false);
      }
      return;
    }
    
    if (!selectedConversation) return;

    setSending(true);
    try {
      const recipientId = selectedConversation.participants.find(p => p !== user.id);
      await axios.post(
        `${API}/messages`,
        {
          recipient_id: recipientId,
          job_id: selectedConversation.job_id,
          content: newMessage.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage("");
      fetchMessages(selectedConversation.id);
      fetchConversations(); // Update unread counts
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const getOtherParticipantName = (conv) => {
    const otherId = conv.participants.find(p => p !== user?.id);
    return conv.participant_names?.[otherId] || "Unknown";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-forest" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      <Header />
      
      <main className="pt-20 pb-12" data-testid="messages-page">
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="font-heading font-bold text-2xl text-gray-900 mb-6">
            Messages
          </h1>

          <div className="grid md:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
            {/* Conversations List */}
            <Card className="md:col-span-1 border-stone-200 overflow-hidden">
              <CardContent className="p-0">
                <div className="p-4 border-b border-stone-200">
                  <h2 className="font-semibold text-gray-900">Conversations</h2>
                </div>
                <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
                  {conversations.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No conversations yet</p>
                      <p className="text-sm mt-1">Messages with professionals will appear here</p>
                    </div>
                  ) : (
                    conversations.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => setSelectedConversation(conv)}
                        className={`w-full p-4 text-left border-b border-stone-100 hover:bg-stone-50 transition-colors ${
                          selectedConversation?.id === conv.id ? "bg-forest/5 border-l-4 border-l-forest" : ""
                        }`}
                        data-testid={`conversation-${conv.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center">
                              <User className="w-5 h-5 text-forest" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {getOtherParticipantName(conv)}
                              </p>
                              <p className="text-sm text-gray-500 truncate max-w-[150px]">
                                {conv.last_message || "No messages yet"}
                              </p>
                            </div>
                          </div>
                          {conv.unread_count > 0 && (
                            <Badge className="bg-red-500 text-white">
                              {conv.unread_count}
                            </Badge>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Messages */}
            <Card className="md:col-span-2 border-stone-200 flex flex-col overflow-hidden">
              {selectedConversation ? (
                <>
                  {/* Header */}
                  <div className="p-4 border-b border-stone-200 flex items-center gap-3">
                    <button
                      onClick={() => setSelectedConversation(null)}
                      className="md:hidden p-2 hover:bg-stone-100 rounded-full"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-forest" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {getOtherParticipantName(selectedConversation)}
                      </p>
                      {selectedConversation.job_id && (
                        <p className="text-sm text-gray-500">Re: Job inquiry</p>
                      )}
                    </div>
                  </div>

                  {/* Messages List */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-stone-50">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === user?.id ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                            msg.sender_id === user?.id
                              ? "bg-forest text-white rounded-br-sm"
                              : "bg-white border border-stone-200 rounded-bl-sm"
                          }`}
                        >
                          <p className={msg.sender_id === user?.id ? "text-white" : "text-gray-900"}>
                            {msg.content}
                          </p>
                          <div className={`flex items-center gap-1 mt-1 text-xs ${
                            msg.sender_id === user?.id ? "text-white/70" : "text-gray-400"
                          }`}>
                            <Clock className="w-3 h-3" />
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            {msg.sender_id === user?.id && msg.read && (
                              <CheckCheck className="w-3 h-3 ml-1" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <form onSubmit={sendMessage} className="p-4 border-t border-stone-200 bg-white">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1"
                        data-testid="message-input"
                      />
                      <Button
                        type="submit"
                        className="bg-forest hover:bg-forest-dark rounded-full px-6"
                        disabled={sending || !newMessage.trim()}
                        data-testid="send-message-btn"
                      >
                        {sending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </form>
                </>
              ) : pendingContact ? (
                <>
                  {/* New Conversation Header */}
                  <div className="p-4 border-b border-stone-200 flex items-center gap-3">
                    <button
                      onClick={() => setPendingContact(null)}
                      className="p-2 hover:bg-stone-100 rounded-full"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-forest" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">New Message</p>
                      {pendingContact.jobId && (
                        <p className="text-sm text-gray-500">Re: Job inquiry</p>
                      )}
                    </div>
                  </div>

                  {/* Empty messages area for new conversation */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-stone-50">
                    <div className="text-center text-gray-500 py-8">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Start a new conversation</p>
                      <p className="text-sm">Send a message to begin chatting</p>
                    </div>
                  </div>

                  {/* Message Input for new conversation */}
                  <form onSubmit={sendMessage} className="p-4 border-t border-stone-200 bg-white">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your first message..."
                        className="flex-1"
                        data-testid="new-message-input"
                        autoFocus
                      />
                      <Button
                        type="submit"
                        className="bg-forest hover:bg-forest-dark rounded-full px-6"
                        disabled={sending || !newMessage.trim()}
                        data-testid="send-first-message-btn"
                      >
                        {sending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">Select a conversation</p>
                    <p className="text-sm">Choose a conversation to view messages</p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
